import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const CartOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const CartContainer = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 30px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  &:hover {
    color: #333;
  }
`;

const Title = styled.h2`
  text-align: center;
  color: #333;
  margin-bottom: 30px;
  font-size: 28px;
`;

const CartItem = styled.div`
  display: flex;
  align-items: center;
  padding: 15px;
  border: 1px solid #eee;
  border-radius: 10px;
  margin-bottom: 10px;
  background: #f9f9f9;
`;

const ItemImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  margin-right: 15px;
`;

const ItemDetails = styled.div`
  flex: 1;
`;

const ItemName = styled.h4`
  margin: 0 0 5px 0;
  color: #333;
`;

const ItemPrice = styled.p`
  margin: 0;
  color: #ff6b6b;
  font-weight: bold;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const QuantityButton = styled.button`
  background: #ff6b6b;
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  cursor: pointer;
  font-size: 16px;
  &:hover {
    background: #ff5252;
  }
`;

const QuantityInput = styled.input`
  width: 50px;
  text-align: center;
  border: 1px solid #ddd;
  border-radius: 5px;
  padding: 5px;
`;

const RemoveButton = styled.button`
  background: #ff4757;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 5px 10px;
  cursor: pointer;
  margin-left: 10px;
  &:hover {
    background: #ff3742;
  }
`;

const TotalSection = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-radius: 10px;
  margin: 20px 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 16px;
`;

const TotalAmount = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 20px;
  font-weight: bold;
  color: #ff6b6b;
  border-top: 2px solid #ddd;
  padding-top: 10px;
  margin-top: 10px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const ContinueButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 15px 30px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  flex: 1;
  &:hover {
    background: #5a6268;
  }
`;

const BackToStoreButton = styled.button`
  background: #ffd93d;
  color: #333;
  border: none;
  border-radius: 10px;
  padding: 15px 30px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  flex: 1;
  &:hover {
    background: #ffc107;
  }
`;

const CheckoutButton = styled.button`
  background: #ff6b6b;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 15px 30px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  flex: 2;
  &:hover {
    background: #ff5252;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const CartPreview = ({ isOpen, onClose, cart, onUpdateCart, onRemoveFromCart, onProceedToCheckout, onBackToStore }) => {
  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => {
      let price = item.price;
      if (typeof price === 'string') {
        // Remove $ and any non-numeric characters except decimal point
        price = price.replace(/[^0-9.]/g, '');
      }
             const numericPrice = parseFloat(price) || 13.99;
      return sum + (numericPrice * item.quantity);
    }, 0);
  };

  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    onUpdateCart(itemId, newQuantity);
  };

  const handleProceedToCheckout = () => {
    onClose(); // Close cart preview
    onProceedToCheckout(); // Open checkout
  };

  if (!isOpen) return null;

  return (
    <CartOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <CartContainer
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
      >
        <CloseButton onClick={onClose}>&times;</CloseButton>
        <Title>🛒 Your Cart</Title>

        {cart.length === 0 ? (
          <EmptyCart>
            <h3>Your cart is empty</h3>
            <p>Add some products to get started!</p>
          </EmptyCart>
        ) : (
          <>
            {cart.map((item) => (
              <CartItem key={item.id}>
                <ItemImage src={item.image} alt={item.name} />
                <ItemDetails>
                  <ItemName>{item.name}</ItemName>
                  <ItemPrice>{item.price}</ItemPrice>
                </ItemDetails>
                <QuantityControl>
                  <QuantityButton 
                    onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                  >
                    -
                  </QuantityButton>
                  <QuantityInput
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value))}
                    min="1"
                  />
                  <QuantityButton 
                    onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                  >
                    +
                  </QuantityButton>
                </QuantityControl>
                <RemoveButton onClick={() => onRemoveFromCart(item.id)}>
                  Remove
                </RemoveButton>
              </CartItem>
            ))}

            <TotalSection>
              <TotalRow>
                <span>Subtotal:</span>
                <span>${calculateSubtotal().toFixed(2)}</span>
              </TotalRow>
              <div style={{
                fontSize: '14px',
                color: '#666',
                textAlign: 'center',
                marginTop: '10px',
                padding: '10px',
                backgroundColor: '#f8f9fa',
                borderRadius: '5px',
                border: '1px solid #e9ecef'
              }}>
                💳 Shipping will be calculated at checkout
              </div>
              <TotalAmount>
                <span>Total:</span>
                <span>${calculateSubtotal().toFixed(2)}</span>
              </TotalAmount>
            </TotalSection>

            <ButtonGroup>
              {onBackToStore && (
                <BackToStoreButton onClick={onBackToStore}>
                  Back to Store
                </BackToStoreButton>
              )}
              <ContinueButton onClick={onClose}>
                Continue Shopping
              </ContinueButton>
              <CheckoutButton onClick={handleProceedToCheckout}>
                Proceed to Checkout
              </CheckoutButton>
            </ButtonGroup>
          </>
        )}
      </CartContainer>
    </CartOverlay>
  );
};

export default CartPreview;
